import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Always use /api for same-domain deployment on Vercel
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Message sent successfully! We will get back to you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred. Please try again or contact us directly at 07576 327340.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper contact-page">
      <SEO 
        title="Contact San Kai Karate Clacton-on-Sea | Get in Touch | Karate Classes Essex"
        description="Contact San Kai Karate Association in Clacton-on-Sea for karate class enquiries. Affordable martial arts training for all ages. Call now or fill out our contact form!"
        keywords="contact karate Clacton, karate enquiries Clacton-on-Sea, karate classes contact Essex, join karate Clacton, martial arts enquiries"
        canonicalPath="/contact"
      />
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Get in touch with us to start your karate journey or ask any questions you may have.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="contact-layout">
          {/* Contact Form */}
          <div className="contact-form-section">
            <h2 className="section-title">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter your name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="01255 123456"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-textarea"
                  placeholder="Tell us about your interest in karate or any questions you have..."
                ></textarea>
              </div>

              <button type="submit" className="btn-primary btn-large" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="contact-info-section">
            <h2 className="section-title">Visit Our Dojo</h2>
            
            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <h3 className="contact-info-title">Training Location</h3>
                <p className="contact-info-text">
                  <strong>Friday:</strong><br />
                  Brotherhood Hall<br />
                  St Oysth Road, Clacton-on-Sea
                </p>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <h3 className="contact-info-title">Phone</h3>
                <p className="contact-info-text">
                  Paul Rees<br />
                  07576 327340
                </p>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <h3 className="contact-info-title">Social Media</h3>
                <p className="contact-info-text">
                  Find us on Facebook:<br />
                  San Kai Karate
                </p>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <Clock size={24} />
                </div>
                <h3 className="contact-info-title">Class Times</h3>
                <p className="contact-info-text">
                  Friday: 6:30 PM - 8:30 PM<br />
                  <br />
                  First session FREE!
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="map-container">
              <iframe
                title="San Kai Karate Locations"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d39413.98676326889!2d1.1247831!3d51.789543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d90f2b3c3c3c3d%3A0x3c3c3c3c3c3c3c3c!2sClacton-on-Sea!5e0!3m2!1sen!2suk!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <p style={{ marginTop: '12px', fontSize: '14px', color: 'var(--text-light)', textAlign: 'center' }}>
                We train at Brotherhood Hall, St Oysth Road, Clacton-on-Sea. Contact Paul for exact directions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
